import { enUS } from 'date-fns/locale/en-US';
import { pt } from 'date-fns/locale/pt';
import { formatInTimeZone } from 'date-fns-tz';
import { type AbilityDefinition, Currency, UserType } from '@/constants';
import { currencyDictionary } from '@/stores/simpleDictionary/currency';

export const useUserStore = defineStore(
    'user',
    () => {
        const login = ref<string | null>(null);
        const id = ref<number | null>(null);
        const type = ref<UserType | null>(null);
        const currency = reactive({
            id: Currency.Euro,
            symbol: currencyDictionary.itemById(Currency.Euro).symbol,
        });
        const timeZone = ref<string>('Europe/London');
        const lang = ref<string | null>(null);
        const owner_id = ref<number | null>(null);
        const locale = ref<string | null>(null);

        const token = ref<string | null>(null);

        function isLoggedIn(): boolean {
            return !!token.value && !!login.value;
        }

        function getTimezoneOffset(): string {
            //"+02:00"
            return formatInTimeZone(new Date(), timeZone.value, 'XXX');
        }

        const dateFnsLocale = computed(() => {
            //todo check it, lang does not work
            return lang.value === 'pt' ? pt : enUS;
        });

        function isRoot(): boolean {
            return type.value === UserType.Root;
        }

        function isAdmin(): boolean {
            return isRoot() || type.value === UserType.Admin;
        }

        function isAgent(): boolean {
            return type.value === UserType.Agent || type.value === UserType.AgentView;
        }

        function isAgentView(): boolean {
            return type.value === UserType.AgentView;
        }

        function isAgentPlatform(): boolean {
            return type.value === UserType.AgentPlatform;
        }

        function isCompany(): boolean {
            return type.value === UserType.Company;
        }

        function hasRole(role: UserType | UserType[]): boolean {
            if (isRoot()) return true;

            const roles = Array.isArray(role) ? role : [role];

            return roles.some((role: UserType) => {
                return type.value === role;
            });
        }

        function can(roles: AbilityDefinition): boolean {
            if (type.value == null) {
                return false;
            }

            if (isRoot()) {
                return true;
            }

            return roles.includes(type.value);
        }

        return {
            login,
            id,
            type,
            currency,
            timeZone,
            lang,
            owner_id,
            locale,
            token,
            isLoggedIn,
            getTimezoneOffset,
            dateFnsLocale,
            isRoot,
            isAdmin,
            isAgent,
            isAgentView,
            isAgentPlatform,
            isCompany,
            hasRole,
            can,
        };
    },
    {
        persist: true,
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
}
